import { apiFetch, ApiError } from './fetcher';

/**
 * React Query keys + fetchers for dashboard read views.
 *
 * Keys follow the convention `[resource, ...args]` so partial
 * invalidation works (e.g., `queryClient.invalidateQueries({
 * queryKey: ['account'] })` busts every account-shaped query).
 *
 * The response types here are deliberately the SPA-side projection,
 * not the full backend response shape — we pull only the fields the
 * dashboard renders.
 *
 * Brief 82 stripped the verification-product fetchers + types
 * (Brief 81 had already stripped the envelope-product surface).
 * What remains is the chassis: account + webhooks + billing +
 * per-product enrollment primitive.
 */

// ---------------------------------------------------------------------------
// /v1/account
// ---------------------------------------------------------------------------

export interface Account {
  accountId: string;
  email: string;
  name: string | null;
  apiKeyPrefix: string | null;
  balance: string;
  balanceMicros: string;
  balanceFormatted: string;
  balancePrecise: string;
  available?: string;
  availableMicros?: string;
  availableFormatted?: string;
  availablePrecise?: string;
  suspended: boolean;
  /** Brief 258 — whether a Telegram chat is bound to this customer (Courier). */
  telegramConnected?: boolean;
  createdAt: string;
}

export const accountKey = ['account'] as const;

export async function fetchAccount(): Promise<Account> {
  return apiFetch<Account>('/v1/account', { noAuthRedirect: true });
}

// Brief 258 — mint a Telegram connect deep-link. Works for any signed-in
// session (wallet-JWT, OAuth, api-key). The human taps the link → /start binds
// their Telegram to this customer → the agent's Courier sends deliver here.
export interface TelegramConnectToken {
  deepLink: string;
  botUsername: string;
  expiresAt: string;
  ttlSeconds: number;
}
export async function createTelegramConnectToken(): Promise<TelegramConnectToken> {
  return apiFetch<TelegramConnectToken>('/v1/account/telegram/connect-token', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ---------------------------------------------------------------------------
// /v1/webhooks
// ---------------------------------------------------------------------------

// Brief 131 — the event allowlist is read live from GET /v1/webhooks/events
// (public discovery) so the dashboard's checkbox list can't drift from the
// server's VALID_EVENTS. The hardcoded WEBHOOK_EVENTS = [] constant this
// replaced had drifted (server had 4 live events, client showed none).
export const webhookEventsKey = ['webhook-events'] as const;
export async function fetchWebhookEvents(): Promise<string[]> {
  const r = await apiFetch<{ events: string[] }>('/v1/webhooks/events', {
    noAuthRedirect: true,
  });
  return r.events ?? [];
}

export type WebhookEvent = string;

export interface Webhook {
  id: string;
  url: string;
  events: WebhookEvent[];
  description: string | null;
  active: boolean;
  consecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookCreateResponse extends Webhook {
  secret: string;
}

export interface WebhookListResponse {
  data: Webhook[];
  total: number;
}

export const webhookListKey = ['webhooks'] as const;
export const webhookDetailKey = (id: string) =>
  ['webhooks', 'detail', id] as const;
export const webhookDeliveriesKey = (
  id: string,
  args: { limit: number; offset: number; status?: string },
) => ['webhooks', 'deliveries', id, args] as const;

export async function fetchWebhooks(): Promise<WebhookListResponse> {
  return apiFetch<WebhookListResponse>('/v1/webhooks');
}

export interface CreateWebhookInput {
  url: string;
  events: WebhookEvent[];
  description?: string;
}

export async function createWebhook(
  input: CreateWebhookInput,
): Promise<WebhookCreateResponse> {
  return apiFetch<WebhookCreateResponse>('/v1/webhooks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface UpdateWebhookInput {
  url?: string;
  events?: WebhookEvent[];
  description?: string;
  active?: boolean;
}

export async function updateWebhook(
  id: string,
  input: UpdateWebhookInput,
): Promise<Webhook> {
  return apiFetch<Webhook>(`/v1/webhooks/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteWebhook(id: string): Promise<void> {
  await apiFetch(`/v1/webhooks/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ── Connected agents (Brief 154 — MCP OAuth connections) ─────────────────────

/** Brief 328 — one registered destination a connection's codes can be sent to. */
export interface ConnectionDestination {
  /** `claude.ai`, `localhost:8765`, `cursor://anysphere.cursor-retrieval`, ... */
  display: string;
  kind: 'web' | 'loopback' | 'app' | 'unparseable';
}

export interface AgentConnection {
  id: string;
  /**
   * Best-effort client name from DCR (e.g. "Claude Code", "Codex"). Nullable.
   * SELF-DECLARED and unverified — anyone can register any name (Brief 328).
   * Always show it next to `destinations`, never alone.
   */
  label: string | null;
  scope: string;
  /**
   * Brief 328 — the addresses this client registered to receive authorization
   * codes at, decoded from its signed client_id. Unforgeable, unlike `label`.
   * Empty when the client_id no longer verifies, or on an older API build.
   */
  destinations?: ConnectionDestination[];
  createdAt: string;
  lastUsedAt: string | null;
  /** Total this connection has spent, in micros (string). "0" when none. */
  spentMicros: string;
}

export interface AgentConnectionListResponse {
  data: AgentConnection[];
}

export const connectionsListKey = ['connections'] as const;

export async function fetchConnections(): Promise<AgentConnectionListResponse> {
  return apiFetch<AgentConnectionListResponse>('/v1/account/connections');
}

export async function revokeConnection(id: string): Promise<void> {
  await apiFetch(`/v1/account/connections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  event: string;
  // Brief 81 — wire field renamed from `envelopeId` to product-agnostic
  // `subjectId` alongside the envelope strip. Stays nullable: the
  // delivery row's underlying column is set when the producing event
  // has a subject, null otherwise.
  subjectId: string | null;
  status: string;
  attempts: number;
  lastAttemptAt: string | null;
  nextRetryAt: string | null;
  responseCode: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface WebhookDeliveriesResponse {
  data: WebhookDelivery[];
  total: number;
  limit: number;
  offset: number;
}

export async function fetchWebhookDeliveries(
  id: string,
  args: { limit: number; offset: number; status?: string },
): Promise<WebhookDeliveriesResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(args.limit));
  params.set('offset', String(args.offset));
  if (args.status) params.set('status', args.status);
  return apiFetch<WebhookDeliveriesResponse>(
    `/v1/webhooks/${encodeURIComponent(id)}/deliveries?${params.toString()}`,
  );
}

// Brief 82 stripped /v1/verifications/* fetchers + types alongside the
// verification product. The chassis query surface (account, webhooks,
// billing, products) remains.

// ---------------------------------------------------------------------------
// POST /v1/account/products/:product/enable — chassis primitive.
// ---------------------------------------------------------------------------

export interface EnableProductResponse {
  ok: true;
  product: string;
  enabled: true;
}

export async function enableProduct(
  product: string,
): Promise<EnableProductResponse> {
  return apiFetch<EnableProductResponse>(
    `/v1/account/products/${encodeURIComponent(product)}/enable`,
    { method: 'POST' },
  );
}

// ---------------------------------------------------------------------------
// GET /v1/account/products — brief 29 / closes L11.
//
// Returns the calling customer's enrollment list. VerificationList queries
// this on mount to decide between the CTA and the list UI.
// ---------------------------------------------------------------------------

export interface AccountProductEntry {
  product: string;
  enabledAt: string;
}

export interface AccountProductsResponse {
  products: AccountProductEntry[];
}

export const accountProductsKey = () => ['account', 'products'] as const;

export async function fetchAccountProducts(): Promise<AccountProductsResponse> {
  return apiFetch<AccountProductsResponse>('/v1/account/products');
}

// ---------------------------------------------------------------------------
// POST /v1/account/api-keys/regenerate — Brief 40.
//
// Mints a fresh API key, returning the raw key ONCE. Caller must
// surface it via a one-time-reveal modal and discard the in-memory
// copy on dismissal. The customer's prior key is revoked best-effort
// on the server side; subsequent calls with the old key return 401.
// ---------------------------------------------------------------------------

export interface RegenerateApiKeyResponse {
  vfApiKey: string;
  apiKeyPrefix: string;
  regeneratedAt: string;
}

export async function regenerateApiKey(
  reason?: string,
): Promise<RegenerateApiKeyResponse> {
  return apiFetch<RegenerateApiKeyResponse>(
    '/v1/account/api-keys/regenerate',
    {
      method: 'POST',
      ...(reason ? { body: JSON.stringify({ reason }) } : {}),
      // 401 here means "old session expired mid-flow" — let the fetcher
      // bounce to /signin per its normal behavior.
    },
  );
}

// ---------------------------------------------------------------------------
// /v1/billing/* — Stripe top-up checkout flow (T.B.6 / brief 49).
// ---------------------------------------------------------------------------

export interface Pack {
  id: string;
  amountMicros: string;
  label: string;
}

export interface PacksResponse {
  packs: Pack[];
  customAmount: {
    minMicros: string;
    maxMicros: string;
  };
}

export const billingPacksKey = ['billing', 'packs'] as const;

export async function fetchBillingPacks(): Promise<PacksResponse> {
  return apiFetch<PacksResponse>('/v1/billing/packs', {
    // Public-ish endpoint; the route is on PUBLIC_AUTH_PATHS so we
    // never see a 401 here. Disable the redirect just in case.
    noAuthRedirect: true,
  });
}

export interface CheckoutRequestPack {
  packId: string;
  successUrl: string;
  cancelUrl: string;
}
export interface CheckoutRequestCustom {
  customAmountMicros: string;
  label?: string;
  successUrl: string;
  cancelUrl: string;
}
export type CheckoutRequest = CheckoutRequestPack | CheckoutRequestCustom;

export interface CheckoutResponse {
  sessionId: string;
  checkoutUrl: string;
}

export async function createCheckout(
  body: CheckoutRequest,
): Promise<CheckoutResponse> {
  return apiFetch<CheckoutResponse>('/v1/billing/checkout', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Baton — Phase 8 (brief 112). The signed-in dashboard's product surface.
//
// Ids are the canonical `bat_<uuid>` form the API returns everywhere; the
// `/v1/baton/:id` routes accept it (they strip the `bat_` prefix), so the
// SPA uses one id form in URLs and fetches. Billable mutations (create /
// extend / add) carry a client-minted `Idempotency-Key` (chassis-mandatory).
// ---------------------------------------------------------------------------

function idempotencyKey(): string {
  return crypto.randomUUID();
}

export interface BatonBundleResources {
  preset: string;
  tier: string;
  priceMicros: string;
  maxSizeBytes: string;
  durationSeconds: string;
  maxEgressBytes: string;
  writesAllowed: number | null;
  readsAllowed: number | null;
}

export interface BatonBundleConsumed {
  egressBytes: string;
  writesUsed: string;
}

export interface BatonCalculatedSummary {
  bytesRemaining: string;
  secondsRemaining: number;
  egressRemainingGb: number;
  writesRemaining: number | null;
}

// Matches the API's WarningEntry: a stable `code` + a free-form `details`
// bag. The dashboard maps the code to human copy (see lib/baton-format.ts
// `warningLabel`); `details` carries the per-warning numbers.
export interface BatonWarning {
  code: string;
  details?: Record<string, unknown>;
}

// ----- GET /v1/baton (list) -----

export interface BatonListItem {
  id: string;
  batonType: string;
  preset: string;
  tier: string;
  status: string;
  name: string | null;
  trustState: string;
  createdAt: string;
  expiresAt: string;
  priceMicros: string;
  sizeBytes: string;
  calculated: BatonCalculatedSummary;
  // Brief 115 — per-baton low-resource warnings (egress/writes/size/time
  // + account_balance_negative). Drives the Batons-list row indicator.
  warnings: BatonWarning[];
}

export interface BatonListResponse {
  data: BatonListItem[];
  total: number;
  limit: number;
  offset: number;
}

export const batonListKey = (args: { limit: number; offset: number }) =>
  ['batons', 'list', args] as const;

export async function fetchBatons(args: {
  limit: number;
  offset: number;
}): Promise<BatonListResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(args.limit));
  params.set('offset', String(args.offset));
  return apiFetch<BatonListResponse>(`/v1/baton?${params.toString()}`);
}

// ----- GET /v1/baton/:id (read content) -----

export interface BatonReadData {
  id: string;
  batonType: string;
  contentType: string;
  contentEncoding: 'utf-8' | 'base64' | 'binary';
  sizeBytes: string;
  version: number;
  deleteState: string;
  bundleResources: BatonBundleResources;
  bundleConsumed: BatonBundleConsumed;
  /** Inline content when ≤ the single-call threshold: UTF-8 text, or base64 for binary/base64 encodings. */
  content?: string;
}

export interface BatonReadResponse {
  data: BatonReadData;
  warnings: BatonWarning[];
  meta?: {
    presignedDownloadUrl?: string;
    presignedDownloadExpiresInSeconds?: number;
  };
}

export const batonReadKey = (id: string) => ['batons', 'read', id] as const;

export async function fetchBaton(id: string): Promise<BatonReadResponse> {
  return apiFetch<BatonReadResponse>(`/v1/baton/${encodeURIComponent(id)}`);
}

// ----- GET /v1/baton/:id/meta (owner metadata + calculated) -----

export interface BatonMetaCalculated {
  bytesRemaining: string;
  secondsRemaining: number;
  egressRemainingGb: number;
  readsRemaining: number | null;
  writesRemaining: number | null;
  activeTokenCount: number;
  inTombstoneWindow: boolean;
  chainLength: number | null;
  estimatedRemainingValueCents: number | null;
  expiresAt: string;
}

export interface BatonMetaData {
  id: string;
  createdByCustomerId: string;
  ownerCustomerId: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  batonType: string;
  contentType: string;
  contentEncoding: string;
  sizeBytes: string;
  checksum: string | null;
  bundleTier: string;
  bundleResources: BatonBundleResources;
  bundleConsumed: BatonBundleConsumed;
  deleteState: string;
  deleteAt: string | null;
  tombstoneUntil: string | null;
  onExpiration: string;
  visibility: string;
  trustState: string | null;
  notarizedAt: string | null;
  notarySignature: string | null;
  chainAnchorHash: string | null;
  hashChaining: boolean;
  // Brief 129 — the committed witness flag, for the document-witness
  // confirm dialog's definite charge copy. Optional: a /meta response
  // fetched before the brief-129 api deploy won't carry it (the dialog
  // falls back defensively).
  flagsSnapshot?: {
    witness: { enabled: boolean; prepaid: boolean; pricePerFireMicros: number };
  };
  name: string | null;
  description: string | null;
  tags: string[];
  calculated: BatonMetaCalculated;
}

export interface BatonMetaResponse {
  data: BatonMetaData;
  warnings: BatonWarning[];
  meta?: Record<string, unknown>;
}

export const batonMetaKey = (id: string) => ['batons', 'meta', id] as const;

export async function fetchBatonMeta(id: string): Promise<BatonMetaResponse> {
  return apiFetch<BatonMetaResponse>(
    `/v1/baton/${encodeURIComponent(id)}/meta`,
  );
}

// ----- POST /v1/baton (create) -----

export interface BatonCustomShape {
  sizeBytes: number;
  durationSeconds: number;
  maxEgressBytes: number;
  /** Brief 118 — passes/writes cap on a custom shape; omit = uncapped. */
  writesAllowed?: number;
}

export interface CreateBatonInput {
  preset: string;
  tier?: string;
  customShape?: BatonCustomShape;
  /** Brief 118 — create-time collaborator tokens (count × scope from the
   *  calculator). The API's tokens[] accepts the richer tokenIssueSchema;
   *  Brief 120 adds the optional per-token caps the calculator now surfaces
   *  (reads_allowed / expires_at). */
  tokens?: Array<{
    type: 'read' | 'write' | 'read_write';
    reads_allowed?: number;
    expires_at?: string;
  }>;
  /** Brief 118 — order-time trust flags (document-witness / chain-witness). */
  flags?: { hashChaining?: boolean; witness?: boolean };
  /** Brief 121 — create-time lifecycle overrides (the calculator's Lifecycle
   *  controls): disposal (soft/hard) + burn-after-reading. */
  onExpiration?: 'hard_delete' | 'tombstone_then_delete';
  deleteOnAllTokensConsumed?: boolean;
  content?: string;
  contentType?: string;
  contentEncoding?: 'utf-8' | 'base64' | 'binary';
  requestPresignedUpload?: boolean;
  name?: string;
  description?: string;
}

export interface CreateBatonResponse {
  data: {
    id: string;
    batonType: string;
    contentType: string;
    contentEncoding: string;
    sizeBytes: string;
    bundleResources: BatonBundleResources;
    deleteState: string;
    version: number;
  };
  warnings: BatonWarning[];
  meta?: {
    presignedUploadUrl?: string;
    presignedUploadExpiresInSeconds?: number;
  };
}

export async function createBaton(
  input: CreateBatonInput,
): Promise<CreateBatonResponse> {
  return apiFetch<CreateBatonResponse>('/v1/baton', {
    method: 'POST',
    body: JSON.stringify(input),
    headers: { 'Idempotency-Key': idempotencyKey() },
  });
}

/**
 * PUT the file bytes to the presigned S3 URL the create response handed
 * back (the >threshold path). A raw fetch — NOT `apiFetch`: presigned
 * URLs carry their own auth in the query string and must NOT receive the
 * session cookie or the bearer JWT. Content-Type matches what was sent at
 * create so it agrees with the signed request.
 */
export async function putPresigned(
  url: string,
  body: Blob | ArrayBuffer,
  contentType: string,
): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    body,
    headers: { 'Content-Type': contentType },
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'presigned_upload_failed', await res.text());
  }
}

// ----- POST /v1/baton/:id (append) -----

export interface AppendBatonInput {
  content: string;
  contentType?: string;
  contentEncoding?: 'utf-8' | 'base64' | 'binary';
  overwrite?: boolean;
}

export interface AppendBatonResponse {
  data: {
    id: string;
    sequenceNumber: string;
    sizeBytes: string;
    version: number;
    operation: 'append' | 'overwrite';
    bundleConsumed: BatonBundleConsumed;
  };
  warnings: BatonWarning[];
}

export async function appendBaton(
  id: string,
  input: AppendBatonInput,
): Promise<AppendBatonResponse> {
  return apiFetch<AppendBatonResponse>(`/v1/baton/${encodeURIComponent(id)}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ----- POST /v1/baton/:id/extend (à-la-carte, billable) -----

export interface ExtendBatonInput {
  addSizeBytes?: number;
  addDurationSeconds?: number;
  addEgressBytes?: number;
  addWritesAllowed?: number;
}

export interface ExtendBatonResponse {
  data: { id: string; bundleResources: BatonBundleResources; version: number };
  warnings: BatonWarning[];
}

export async function extendBaton(
  id: string,
  input: ExtendBatonInput,
): Promise<ExtendBatonResponse> {
  return apiFetch<ExtendBatonResponse>(
    `/v1/baton/${encodeURIComponent(id)}/extend`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'Idempotency-Key': idempotencyKey() },
    },
  );
}

// ----- POST /v1/baton/:id/add (additive tier, billable) -----

export interface AddBatonTierResponse {
  data: { id: string; bundleResources: BatonBundleResources; version: number };
  warnings: BatonWarning[];
}

export async function addBatonTier(
  id: string,
  tier: string,
): Promise<AddBatonTierResponse> {
  return apiFetch<AddBatonTierResponse>(
    `/v1/baton/${encodeURIComponent(id)}/add`,
    {
      method: 'POST',
      body: JSON.stringify({ tier }),
      headers: { 'Idempotency-Key': idempotencyKey() },
    },
  );
}

// ----- DELETE /v1/baton/:id -----

export interface DeleteBatonInput {
  at?: string;
  permanent?: boolean;
  tombstone_hours?: number;
}

export interface DeleteBatonResponse {
  data: {
    id: string;
    deleteState: string;
    deleteAt: string | null;
    tombstoneUntil: string | null;
    version: number;
    idempotent: boolean;
  };
  warnings: BatonWarning[];
}

export async function deleteBaton(
  id: string,
  input: DeleteBatonInput = {},
): Promise<DeleteBatonResponse> {
  return apiFetch<DeleteBatonResponse>(`/v1/baton/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    ...(Object.keys(input).length > 0
      ? { body: JSON.stringify(input) }
      : {}),
  });
}

// ---------------------------------------------------------------------------
// Baton collaborator tokens — Phase 8b (brief 113). The detail Tokens tab.
//
// `tokenId` is the full `tok_<43-char>` bearer value (the API stores it
// plaintext as the PK and returns it in full on list — re-viewable, not
// one-time-reveal). All three ops are owner-only management, NOT billable
// (D108 — token ops are not a priced axis), so no Idempotency-Key. Each
// endpoint wraps its result in the `{ data, warnings, meta }` envelope.
// ---------------------------------------------------------------------------

export type BatonTokenType = 'read' | 'write' | 'read_write';

export interface BatonToken {
  tokenId: string;
  batonId: string;
  type: BatonTokenType;
  readsAllowed: number | null;
  writesAllowed: number | null;
  readsUsed: number;
  writesUsed: number;
  expiresAt: string | null;
  createdAt: string;
  revokedAt: string | null;
  createdByCustomerId: string;
  payer: string;
  requireFingerprint: boolean;
  ipAllowList: string[];
  // Brief 201 — the human editor link (read/read_write tokens only). Open it for
  // a human to edit (read_write) or view (read) the doc with no account.
  editUrl?: string;
}

interface TokenEnvelope {
  data: BatonToken;
  warnings: BatonWarning[];
  meta?: Record<string, unknown>;
}
interface TokenListEnvelope {
  data: BatonToken[];
  warnings: BatonWarning[];
  meta?: Record<string, unknown>;
}

export const batonTokensKey = (id: string) => ['batons', 'tokens', id] as const;

export async function fetchBatonTokens(id: string): Promise<BatonToken[]> {
  const res = await apiFetch<TokenListEnvelope>(
    `/v1/baton/${encodeURIComponent(id)}/tokens`,
  );
  return res.data;
}

export interface IssueTokenInput {
  type: BatonTokenType;
  reads_allowed?: number | null;
  writes_allowed?: number | null;
  expires_at?: string | null;
  require_fingerprint?: boolean;
  ip_allow_list?: string[];
}

export async function issueBatonToken(
  id: string,
  input: IssueTokenInput,
): Promise<BatonToken> {
  const res = await apiFetch<TokenEnvelope>(
    `/v1/baton/${encodeURIComponent(id)}/tokens`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return res.data;
}

/** Revoke a token: POST /:id/tokens/:tokenId with `{ revoke: true }`. Idempotent. */
export async function revokeBatonToken(
  id: string,
  tokenId: string,
): Promise<BatonToken> {
  const res = await apiFetch<TokenEnvelope>(
    `/v1/baton/${encodeURIComponent(id)}/tokens/${encodeURIComponent(tokenId)}`,
    { method: 'POST', body: JSON.stringify({ revoke: true }) },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Baton trust quartet — Phase 8c (brief 114). The detail Trust tab.
//
// verify-chain (links) / challenge / proof are free, metadata-only, no egress;
// document-witness is the only billable one ($0.05 — conditionally, free if
// the baton prepaid witnessing) and so carries an Idempotency-Key. All four
// return a bare `{ data }` body (no warnings envelope, except the witness
// wrapper which also returns `{ data }`).
// ---------------------------------------------------------------------------

// ----- POST /:id/trust/verify-chain (mode=links; free, no egress) -----

export interface VerifyChainResult {
  id: string;
  mode: string;
  intact: boolean;
  head?: string;
  brokenAt?: string;
  reason?: string;
  expectedPrev?: string;
  actualPrev?: string;
}

export async function verifyBatonChain(id: string): Promise<VerifyChainResult> {
  const res = await apiFetch<{ data: VerifyChainResult }>(
    `/v1/baton/${encodeURIComponent(id)}/trust/verify-chain`,
    { method: 'POST', body: JSON.stringify({ mode: 'links' }) },
  );
  return res.data;
}

// ----- GET /:id/trust/proof (public, free; the signed attestation) -----

export interface BatonProof {
  formatVersion: string;
  contentHash: string;
  chainHead: string | null;
  witnessSignature: string;
  witnessKeyId: string;
  witnessedAt: string;
}

export const batonProofKey = (id: string) => ['batons', 'proof', id] as const;

export async function fetchBatonProof(id: string): Promise<BatonProof> {
  const res = await apiFetch<{ data: { id: string; documentWitness: BatonProof } }>(
    `/v1/baton/${encodeURIComponent(id)}/trust/proof`,
  );
  return res.data.documentWitness;
}

// ----- POST /:id/trust/challenge (public, free; candidate match) -----

export interface ChallengeResult {
  id: string;
  match: boolean;
  witnessedAt: string | null;
}

export async function challengeBaton(
  id: string,
  candidate: { content: string } | { contentHash: string },
): Promise<ChallengeResult> {
  const res = await apiFetch<{ data: ChallengeResult }>(
    `/v1/baton/${encodeURIComponent(id)}/trust/challenge`,
    { method: 'POST', body: JSON.stringify(candidate) },
  );
  return res.data;
}

// ----- POST /:id/trust/document-witness (billable $0.05, conditional) -----

export interface DocumentWitnessResult {
  id: string;
  contentHash: string;
  witnessSignature: string;
  witnessKeyId: string;
  witnessedAt: string;
}

export async function witnessBaton(id: string): Promise<DocumentWitnessResult> {
  const res = await apiFetch<{ data: DocumentWitnessResult }>(
    `/v1/baton/${encodeURIComponent(id)}/trust/document-witness`,
    {
      method: 'POST',
      body: JSON.stringify({}),
      headers: { 'Idempotency-Key': idempotencyKey() },
    },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Observability — Phase 8d (brief 115). The Activity tab + the Overview
// global activity log + the Billing transaction ledger. All session-scoped,
// read-only, free.
// ---------------------------------------------------------------------------

// ----- GET /v1/account/events (cross-baton; ?batonId= narrows) -----

export interface BatonEvent {
  id: string;
  batonId: string;
  batonName: string | null;
  eventType: string;
  actorType: string;
  actorId: string | null;
  occurredAt: string;
  details: Record<string, unknown> | null;
}

export interface AccountEventsResponse {
  data: BatonEvent[];
  meta: { hasMore: boolean; limit: number; offset: number };
}

export const accountEventsKey = (args: {
  batonId?: string;
  limit: number;
  offset: number;
}) => ['account', 'events', args] as const;

export async function fetchAccountEvents(args: {
  batonId?: string;
  limit: number;
  offset: number;
}): Promise<AccountEventsResponse> {
  const p = new URLSearchParams();
  p.set('limit', String(args.limit));
  p.set('offset', String(args.offset));
  if (args.batonId) p.set('batonId', args.batonId);
  return apiFetch<AccountEventsResponse>(`/v1/account/events?${p.toString()}`);
}

// ----- GET /v1/account/ledger (customer-self-scoped transaction history) -----

export interface LedgerRow {
  id: string;
  direction: 'credit' | 'debit';
  amountMicros: string;
  source: string;
  reason: string;
  serviceKey: string | null;
  status: string;
  subjectType: string | null;
  subjectId: string | null;
  txHash: string | null;
  stripePaymentIntentId: string | null;
  createdAt: string;
}

export interface AccountLedgerResponse {
  data: LedgerRow[];
  total: number;
  limit: number;
  offset: number;
}

export const accountLedgerKey = (args: { limit: number; offset: number }) =>
  ['account', 'ledger', args] as const;

export async function fetchAccountLedger(args: {
  limit: number;
  offset: number;
}): Promise<AccountLedgerResponse> {
  const p = new URLSearchParams();
  p.set('limit', String(args.limit));
  p.set('offset', String(args.offset));
  return apiFetch<AccountLedgerResponse>(`/v1/account/ledger?${p.toString()}`);
}

// ===========================================================================
// Brief 185 — multi-product dashboard: activity stream, home metrics, catalog,
// LLM catalog, and the e-sign / idverify workspace reads.
// ===========================================================================

export interface ActivityActor {
  kind: 'mcp_connection' | 'api_key' | 'session' | 'x402_wallet' | null;
  id: string | null;
}

/** The LLM cost-plus receipt (Brief 184), present only on llm rows. */
export interface LlmReceipt {
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  providerCostMicros?: number;
  markupPct?: number;
  chargedMicros?: number;
  /** Brief 185 — the authorized ceiling carried on the receipt (the captured
   *  ledger row's amount is the actual, so the ceiling rides here, not the row). */
  ceilingMicros?: number;
  truncated?: boolean;
  [k: string]: unknown;
}

/**
 * A money charge — a ledger row that moved the balance. The default activity
 * item; what every row was before Brief 187 Phase 4D.
 */
export interface ActivityMoneyRow {
  kind: 'money';
  id: string;
  direction: 'debit' | 'credit';
  amountMicros: string;
  source: string;
  reason: string;
  serviceKey: string | null;
  product: string | null;
  status: string;
  subjectType: string | null;
  subjectId: string | null;
  /** The op's Idempotency-Key (non-MCP) — groups baton quota draws under it. */
  correlationId: string | null;
  actor: ActivityActor;
  receipt: LlmReceipt | null;
  createdAt: string;
}

/**
 * Brief 187 Phase 4D — a byte-denominated QUOTA event: a cputools op drew a
 * baton's prepaid egress (an INPUT read) or storage (a `deliver` write). NOT a
 * money charge — it draws prepaid quota, never the balance. Grouped under its
 * op's compute charge by `correlationId` when both are on the page.
 */
export interface ActivityQuotaRow {
  kind: 'quota';
  id: string;
  correlationId: string | null;
  /** `bat_`-prefixed baton the bytes were drawn against. */
  batonId: string;
  dimension: 'egress' | 'storage';
  eventType: string;
  /** Byte count drawn (string-encoded; may exceed Number range conceptually). */
  bytes: string;
  via: string;
  product: 'baton';
  createdAt: string;
}

export type ActivityRow = ActivityMoneyRow | ActivityQuotaRow;

export interface ActivityResponse {
  data: ActivityRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface ActivityQuery {
  limit?: number;
  offset?: number;
  /** Filter to one acting credential (the per-agent stream). */
  actor?: string;
  /** Filter to a product family (sugar for service_prefix=<product>.). */
  product?: string;
  /** Filter to a raw service-key prefix (e.g. 'cputools.'). */
  servicePrefix?: string;
}

export const activityKey = (q: ActivityQuery) => ['account', 'activity', q] as const;

export async function fetchActivity(q: ActivityQuery): Promise<ActivityResponse> {
  const p = new URLSearchParams();
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.offset !== undefined) p.set('offset', String(q.offset));
  if (q.actor) p.set('actor', q.actor);
  if (q.product) p.set('product', q.product);
  if (q.servicePrefix) p.set('service_prefix', q.servicePrefix);
  const qs = p.toString();
  return apiFetch<ActivityResponse>(`/v1/account/activity${qs ? `?${qs}` : ''}`);
}

export interface UsedProduct {
  product: string;
  lastUsedAt: string;
  calls: number;
}

export interface AccountMetrics {
  balance: { balanceMicros: string; availableMicros: string };
  batons: { active: number; storageBytes: string; nextExpiry: string | null };
  spend: {
    windowDays: number;
    since: string;
    spentMicros: string;
    calls: number;
    agents: number;
  };
  usedProducts: UsedProduct[];
}

export const accountMetricsKey = ['account', 'metrics'] as const;

export async function fetchAccountMetrics(): Promise<AccountMetrics> {
  return apiFetch<AccountMetrics>('/v1/account/metrics');
}

// ── Product catalog (public discovery) ──────────────────────────────────────
export const catalogKey = ['catalog', 'products'] as const;

/** Shape is intentionally loose — it mirrors the relaystation_products MCP
 *  tool body (product families keyed by name). The Catalog page reads it
 *  defensively. */
export async function fetchCatalog(): Promise<Record<string, unknown>> {
  return apiFetch<Record<string, unknown>>('/v1/catalog/products', {
    noAuthRedirect: true,
  });
}

// ── The One True Catalog (Brief 189) — the rich editorial projection ─────────
export const catalogFullKey = ['catalog', 'full'] as const;

export interface CatalogFullOp {
  opId: string;
  label: string;
  route: string;
  category: string;
  priceKind: 'fixed' | 'quote' | 'metered' | 'cost_plus';
  price: string;
  priceMicros: number | null;
  unit: string | null;
}
export interface CatalogFull {
  opCount: number;
  categoryCount: number;
  categories: Array<{ name: string; featured: boolean; count: number; ops: CatalogFullOp[] }>;
  ops: CatalogFullOp[];
}

/** GET /v1/catalog — the baked editorial snapshot (129 ops, prices, priceKind). */
export async function fetchCatalogFull(): Promise<CatalogFull> {
  return apiFetch<CatalogFull>('/v1/catalog', { noAuthRedirect: true });
}

// ── LLM task catalog (public) ───────────────────────────────────────────────
export interface LlmTaskTier {
  tier: string;
  ceilingMicrosPerIncrement?: string | number;
}
export interface LlmTask {
  key: string;
  label: string;
  description: string;
  transformClass?: string;
  outputCapTokens?: number;
  tiers: LlmTaskTier[];
}
export interface LlmTasksResponse {
  tasks: LlmTask[];
  pricing: {
    model: string;
    markupPct: number;
    incrementKb: number;
    maxInputKb: number;
    note?: string;
  };
}

export const llmTasksKey = ['llm', 'tasks'] as const;

export async function fetchLlmTasks(): Promise<LlmTasksResponse> {
  return apiFetch<LlmTasksResponse>('/v1/llm/tasks', { noAuthRedirect: true });
}

// ── e-sign workspace (read) ─────────────────────────────────────────────────
export interface EsignEnvelope {
  id: string;
  status?: string;
  title?: string;
  subject?: string;
  createdAt?: string;
  [k: string]: unknown;
}
export interface EsignListResponse {
  data?: EsignEnvelope[];
  envelopes?: EsignEnvelope[];
  total?: number;
  limit?: number;
  offset?: number;
}

export const esignListKey = (args: { limit: number; offset: number }) =>
  ['esign', 'envelopes', args] as const;

export async function fetchEsignEnvelopes(args: {
  limit: number;
  offset: number;
}): Promise<EsignListResponse> {
  const p = new URLSearchParams();
  p.set('limit', String(args.limit));
  p.set('offset', String(args.offset));
  return apiFetch<EsignListResponse>(`/v1/esigndoc/envelopes?${p.toString()}`);
}

// ── idverify workspace (read) ───────────────────────────────────────────────
export interface IdverifyVerification {
  id: string;
  status?: string;
  type?: string;
  createdAt?: string;
  [k: string]: unknown;
}
export interface IdverifyListResponse {
  data?: IdverifyVerification[];
  verifications?: IdverifyVerification[];
  total?: number;
  limit?: number;
  offset?: number;
}

export const idverifyListKey = (args: { limit: number; offset: number }) =>
  ['idverify', 'verifications', args] as const;

export async function fetchIdverifyVerifications(args: {
  limit: number;
  offset: number;
}): Promise<IdverifyListResponse> {
  const p = new URLSearchParams();
  p.set('limit', String(args.limit));
  p.set('offset', String(args.offset));
  return apiFetch<IdverifyListResponse>(
    `/v1/idverify/verifications?${p.toString()}`,
  );
}
